'use client'

import { ReactNode } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../index'

interface ConfirmationModalProps {
  /** The trigger element that opens the modal */
  trigger: ReactNode
  /** The title of the confirmation modal */
  title: string
  /** The description/message shown in the modal */
  description: string | ReactNode
  /** Text for the confirm button */
  confirmText?: string
  /** Text for the cancel button */
  cancelText?: string
  /** Whether the modal is in a loading state */
  isLoading?: boolean
  /** Whether the modal is open */
  open?: boolean
  /** Callback when modal open state changes */
  onOpenChange?: (open: boolean) => void
  /** Callback when confirm is clicked */
  onConfirm: () => void
  /** Optional custom class for the confirm button */
  confirmButtonClassName?: string
  /** Whether to use destructive styling */
  isDestructive?: boolean
  /** Optional custom class for the modal content */
  className?: string
  /** Whether to disable the confirm button */
  isConfirmDisabled?: boolean
}

export function ConfirmationModal({
  trigger,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  open,
  onOpenChange,
  onConfirm,
  confirmButtonClassName,
  isDestructive = false,
  className,
  isConfirmDisabled = false
}: ConfirmationModalProps) {
  const defaultDestructiveClasses = 'bg-destructive hover:bg-destructive/90'

  const confirmButtonClasses = isDestructive
    ? confirmButtonClassName || defaultDestructiveClasses
    : confirmButtonClassName

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className={className}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isLoading || isConfirmDisabled} className={confirmButtonClasses}>
            {isLoading ? 'Loading...' : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
